/**
 * The public inquiry form (no sign-in): /inquire/<firm>. Shared on social media as a link, or shown
 * inside the firm's website (?embed=1), where it tells the page its height so no scrollbar appears.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { CalendarCheck, CheckCircle2, MessageSquare } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Empty, Loading } from "@/components/page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ApiError, errorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";
import { call, Frame, linkTag } from "@/pages/book-public";

type Form = { firmName: string; responseTime: string; bookUrl: string; services: Array<{ key: string; label: string }>; heardFrom: Array<{ key: string; label: string }>; token: string };

const embedded = () => new URLSearchParams(window.location.search).get("embed") === "1";

/** Inside a website: keeps the iframe as tall as the form. */
function useReportHeight(ref: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    if (!embedded() || !ref.current || window.parent === window) return;
    const send = () => window.parent.postMessage({ type: "clientflow:height", height: Math.ceil(ref.current!.getBoundingClientRect().height) + 8 }, "*");
    const ro = new ResizeObserver(send);
    ro.observe(ref.current);
    send();
    return () => ro.disconnect();
  }, [ref]);
}

export function PublicInquiryPage({ slug }: { slug: string }) {
  const embed = embedded();
  const ref = useRef<HTMLDivElement>(null);
  useReportHeight(ref);
  const tag = linkTag();
  const { data, isLoading, error } = useQuery<Form>({ queryKey: ["public-inquire", slug], queryFn: () => call(`/api/public/leads/${encodeURIComponent(slug)}`), retry: false, staleTime: Infinity });
  const [v, setV] = useState({ name: "", email: "", phone: "", kind: "individual" as "individual" | "business", company: "", message: "", heardFrom: "", referredBy: "", website: "" });
  const [services, setServices] = useState<string[]>([]);
  const send = useMutation({
    mutationFn: () =>
      call<{ firstName: string }>(`/api/public/leads/${encodeURIComponent(slug)}`, {
        method: "POST",
        body: JSON.stringify({
          token: data!.token,
          ...v,
          services,
          source: tag.source ?? "",
          utmSource: "",
          campaign: tag.campaign ?? "",
          // Inside a website, the page it sits on.
          page: (embed ? document.referrer : window.location.href).slice(0, 500),
        }),
      }),
  });

  const wrap = (children: React.ReactNode) =>
    embed ? (
      <div ref={ref} className="bg-transparent p-1">
        {children}
      </div>
    ) : (
      <div ref={ref}>
        <Frame firm={data?.firmName ?? "Contact us"} icon={MessageSquare}>
          {children}
        </Frame>
      </div>
    );

  if (isLoading) return wrap(<Loading />);
  if (error || !data) return wrap(<Empty>{error instanceof ApiError && error.status === 404 ? "This page doesn't exist. Check the link." : errorMessage(error)}</Empty>);
  const card = "rounded-xl border border-card-border bg-card p-5 shadow-[var(--card-shadow)] sm:p-6";

  if (send.isSuccess)
    return wrap(
      <div className={cn(card, "text-center")}>
        <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
        <h1 className="mt-3 text-xl font-semibold">Thank you{send.data.firstName ? `, ${send.data.firstName}` : ""}</h1>
        <p className="mt-2 text-sm text-muted-foreground">We received your message and will reply {data.responseTime}.</p>
        <p className="mt-5 text-sm">Want to talk sooner?</p>
        <Button asChild className="mt-2">
          <a href={`${data.bookUrl}${tag.source ? `?source=${encodeURIComponent(tag.source)}` : ""}`} target={embed ? "_top" : undefined}>
            <CalendarCheck className="h-4 w-4" /> Choose a time to talk
          </a>
        </Button>
      </div>,
    );

  const toggle = (k: string) => setServices((x) => (x.includes(k) ? x.filter((y) => y !== k) : [...x, k]));
  return wrap(
    <form
      className={cn(card, "space-y-5")}
      onSubmit={(e) => {
        e.preventDefault();
        send.mutate();
      }}
    >
      <div>
        <h1 className="text-xl font-semibold">How can we help?</h1>
        <p className="mt-1 text-sm text-muted-foreground">Tell us a little about what you need. We'll reply {data.responseTime}.</p>
      </div>
      <fieldset className="space-y-2">
        <legend className="mb-1.5 text-sm font-medium">I'm asking for</legend>
        <div className="flex flex-wrap gap-2">
          {(["individual", "business"] as const).map((k) => (
            <button key={k} type="button" aria-pressed={v.kind === k} onClick={() => setV({ ...v, kind: k })} className={cn("rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors", v.kind === k ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:bg-muted")}>
              {k === "individual" ? "Myself or my family" : "My business"}
            </button>
          ))}
        </div>
      </fieldset>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="iq-name">Name</Label>
          <Input id="iq-name" required autoComplete="name" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} />
        </div>
        {v.kind === "business" && (
          <div className="space-y-1">
            <Label htmlFor="iq-company">Business name</Label>
            <Input id="iq-company" autoComplete="organization" value={v.company} onChange={(e) => setV({ ...v, company: e.target.value })} />
          </div>
        )}
        <div className="space-y-1">
          <Label htmlFor="iq-email">Email</Label>
          <Input id="iq-email" type="email" required autoComplete="email" value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="iq-phone">Phone (optional)</Label>
          <Input id="iq-phone" type="tel" autoComplete="tel" value={v.phone} onChange={(e) => setV({ ...v, phone: e.target.value })} />
        </div>
      </div>
      <fieldset>
        <legend className="mb-1.5 text-sm font-medium">What do you need help with?</legend>
        <div className="flex flex-wrap gap-2">
          {data.services.map((x) => (
            <button key={x.key} type="button" aria-pressed={services.includes(x.key)} onClick={() => toggle(x.key)} className={cn("rounded-full border px-3 py-1.5 text-sm transition-colors", services.includes(x.key) ? "border-primary bg-primary/10 font-medium text-primary" : "border-border bg-card hover:bg-muted")}>
              {x.label}
            </button>
          ))}
        </div>
      </fieldset>
      <div className="space-y-1">
        <Label htmlFor="iq-message">Message (optional)</Label>
        <Textarea id="iq-message" rows={4} value={v.message} onChange={(e) => setV({ ...v, message: e.target.value })} placeholder="A few words about your situation" />
      </div>
      {!tag.source && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="iq-heard">How did you hear about us? (optional)</Label>
            <select id="iq-heard" className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={v.heardFrom} onChange={(e) => setV({ ...v, heardFrom: e.target.value })}>
              <option value="">Choose…</option>
              {data.heardFrom.map((h) => (
                <option key={h.key} value={h.key}>
                  {h.label}
                </option>
              ))}
            </select>
          </div>
          {v.heardFrom === "referral" && (
            <div className="space-y-1">
              <Label htmlFor="iq-ref">Who referred you? (optional)</Label>
              <Input id="iq-ref" value={v.referredBy} onChange={(e) => setV({ ...v, referredBy: e.target.value })} />
            </div>
          )}
        </div>
      )}
      {/* Hidden from people; only bots fill it in. */}
      <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
        <label htmlFor="iq-website">Website</label>
        <input id="iq-website" tabIndex={-1} autoComplete="off" value={v.website} onChange={(e) => setV({ ...v, website: e.target.value })} />
      </div>
      {send.error && <p className="text-sm text-destructive">{errorMessage(send.error)}</p>}
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={send.isPending}>
          Send
        </Button>
        <span className="text-xs text-muted-foreground">We use your details only to reply to you.</span>
      </div>
    </form>,
  );
}
